import React, { createContext, useContext, useState, useEffect } from 'react';

const TutorialContext = createContext();

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

export const TutorialProvider = ({ children }) => {
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [showTooltips, setShowTooltips] = useState(true);

  // Load tutorial state from localStorage
  useEffect(() => {
    const completed = localStorage.getItem('onboardingCompleted') === 'true';
    const tooltipsEnabled = localStorage.getItem('showTooltips') !== 'false';
    setHasCompletedOnboarding(completed);
    setShowTooltips(tooltipsEnabled);
    
    // Auto-start onboarding for new users
    if (!completed) {
      // Give a small delay for the app to fully load
      const timer = setTimeout(() => {
        setIsOnboardingActive(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const startOnboarding = () => {
    setIsOnboardingActive(true);
    setCurrentStep(0);
  };

  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
  };

  const previousStep = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  const skipOnboarding = () => {
    setIsOnboardingActive(false);
    setCurrentStep(0);
    localStorage.setItem('onboardingCompleted', 'true');
    setHasCompletedOnboarding(true);
  };

  const completeOnboarding = () => {
    setIsOnboardingActive(false);
    setCurrentStep(0);
    localStorage.setItem('onboardingCompleted', 'true');
    setHasCompletedOnboarding(true);
  };

  const resetOnboarding = () => {
    localStorage.removeItem('onboardingCompleted');
    setHasCompletedOnboarding(false);
    startOnboarding();
  };

  const toggleTooltips = () => {
    const newValue = !showTooltips;
    setShowTooltips(newValue);
    localStorage.setItem('showTooltips', String(newValue));
  };

  const value = {
    isOnboardingActive,
    currentStep,
    hasCompletedOnboarding,
    showTooltips,
    startOnboarding,
    nextStep,
    previousStep,
    skipOnboarding,
    completeOnboarding,
    resetOnboarding,
    toggleTooltips,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export default TutorialContext;

